"use client";

import IconArrowInbox from "@ui/central-icons/icons/IconArrowInbox";
import IconWriting from "@ui/central-icons/icons/IconWriting";
import { useState, useEffect, useRef } from "react";

import { useFormatter, useTranslations } from "next-intl";

import { cn } from "@ui/lib/utils";
import { formatTime } from "@ui/lib/time";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import { Button } from "../core/Button";
import { Link } from "../core/Link";
import type { ImageProps } from "next/image";

import { AudioRowPlayControl } from "../sound-effects/AudioRowPlayControl";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../core/Tooltip";

interface SoundEffectExploreItem {
  id: string;
  title: string;
  thumbnailImageProps?: ImageProps;
  audioUrl?: string;
  duration?: string;
  durationSeconds?: number;
  isLoop?: boolean;
  downloadCount?: number;
  /** 3-4 word display title from LLM summarisation. Present only for XI items once cache is warm. Falls back to title. */
  summary?: string;
}

interface CategoryExploreListLabels {
  headerCountSuffix: string;
  generateCtaDefaultLabel: string;
}

interface CategoryExploreListProps {
  items: SoundEffectExploreItem[];
  categoryName?: string;
  totalCount?: number;
  generateCtaHref?: string;
  generateCtaLabel?: string;
  generateCtaPosition?: number;
  generateCtaShowOnMobile?: boolean;
  generateFooterTitle?: string;
  generateFooterSubtitle?: string;
  onDownload?: (item: SoundEffectExploreItem) => void;
  onEditPrompt?: (item: SoundEffectExploreItem) => void;
  onGenerateSfx?: () => void;
  onPlay?: (item: SoundEffectExploreItem) => void;
  durationFormat?: "time" | "seconds";
  labels: CategoryExploreListLabels;
}

function GenerateCtaRow({
  href,
  label,
  onGenerateSfx,
  buttonLabel,
  showOnMobile,
}: {
  href: string;
  label: string;
  onGenerateSfx?: () => void;
  buttonLabel: string;
  showOnMobile?: boolean;
}) {
  return (
    <div
      className={cn(
        "tw-items-center tw-justify-between tw-border-b-2 tw-border-dotted tw-border-black/10 tw-py-3 tw-transition-colors last:tw-border-b-0",
        showOnMobile ? "tw-flex" : "tw-hidden lg:tw-flex",
        "tw-relative tw-cursor-pointer hover:tw-bg-[linear-gradient(90deg,_rgba(245,243,241,0)_0%,_#F5F3F1_8%,_#F5F3F1_92%,_rgba(245,243,241,0)_100%)]",
      )}
    >
      {/* Full-row clickable overlay */}
      {onGenerateSfx ? (
        <button className='tw-absolute tw-inset-0 tw-z-0' onClick={onGenerateSfx} aria-label={buttonLabel} />
      ) : (
        <a className='tw-absolute tw-inset-0 tw-z-0' href={href} aria-label={buttonLabel} />
      )}
      <div className='tw-flex tw-items-center tw-gap-4'>
        <div className='tw-flex tw-size-12 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-sm tw-border tw-border-white/10 tw-bg-gray-100'>
          <IconWriting className={cn(iconStrokeFix, "tw-size-5 tw-text-black")} />
        </div>
        <p className='tw-type-base tw-text-black'>{label}</p>
      </div>
      <div className='tw-relative tw-z-[1]'>
        {onGenerateSfx ? (
          <Button variant='elevated' size='md' onClick={onGenerateSfx}>
            {buttonLabel}
          </Button>
        ) : (
          <Link href={href} asButton buttonVariants={{ variant: "elevated", size: "md" }}>
            {buttonLabel}
          </Link>
        )}
      </div>
    </div>
  );
}

function GenerateFooter({
  href,
  title,
  subtitle,
  onGenerateSfx,
  buttonLabel,
}: {
  href: string;
  title: string;
  subtitle?: string;
  onGenerateSfx?: () => void;
  buttonLabel: string;
}) {
  return (
    <div className='tw-mt-12 tw-flex tw-flex-col tw-items-center tw-gap-6 tw-text-center'>
      <div className='tw-flex tw-flex-col tw-items-center tw-gap-4'>
        <p className='tw-type-xl tw-text-black tw-max-w-sm'>{title}</p>
        {subtitle && <p className='tw-type-sm tw-text-gray-500 tw-max-w-sm'>{subtitle}</p>}
      </div>
      {onGenerateSfx ? (
        <Button variant='elevated' size='lg' onClick={onGenerateSfx}>
          <IconWriting className={cn(iconStrokeFix, "tw-size-4")} />
          {buttonLabel}
        </Button>
      ) : (
        <Link href={href} asButton buttonVariants={{ variant: "elevated", size: "lg" }}>
          <IconWriting className={cn(iconStrokeFix, "tw-size-4")} />
          {buttonLabel}
        </Link>
      )}
    </div>
  );
}

function AudioRow({
  item,
  showDownloadsColumn,
  onDownload,
  onEditPrompt,
  onPlay,
  durationFormat = "time",
}: {
  item: SoundEffectExploreItem;
  showDownloadsColumn: boolean;
  onDownload?: (item: SoundEffectExploreItem) => void;
  onEditPrompt?: (item: SoundEffectExploreItem) => void;
  onPlay?: (item: SoundEffectExploreItem) => void;
  durationFormat?: "time" | "seconds";
}) {
  const t = useTranslations("ClientKeys.Globals");
  const format = useFormatter();

  const rowRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [fetchedDuration, setFetchedDuration] = useState<string | null>(null);
  const [fetchedDurationSeconds, setFetchedDurationSeconds] = useState<number | null>(null);

  useEffect(() => {
    const el = rowRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "50px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    if ((item.duration && item.durationSeconds != null) || !item.audioUrl) return;

    const audio = new Audio();
    audio.preload = "metadata";

    const onLoadedMetadata = () => {
      if (isFinite(audio.duration)) {
        setFetchedDuration(formatTime(audio.duration));
        setFetchedDurationSeconds(audio.duration);
      }
    };

    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.src = item.audioUrl;

    return () => {
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.src = "";
    };
  }, [isVisible, item.duration, item.durationSeconds, item.audioUrl]);

  const metaParts: string[] = [];
  const displayDuration = (() => {
    if (durationFormat === "seconds") {
      const secs = item.durationSeconds ?? fetchedDurationSeconds;
      return secs != null ? `${secs.toFixed(1)}s` : null;
    }
    return item.duration || fetchedDuration || "00:00";
  })();
  if (displayDuration) metaParts.push(displayDuration);
  if (item.isLoop != null) metaParts.push(item.isLoop ? t("loop") : t("no_loop"));
  const metaText = metaParts.join(" \u2022 ");

  return (
    <div
      ref={rowRef}
      className={cn(
        "[&:hover:not(:has([data-action-buttons]:hover))]:tw-bg-[linear-gradient(90deg,_rgba(245,243,241,0)_0%,_#F5F3F1_8%,_#F5F3F1_92%,_rgba(245,243,241,0)_100%)] tw-group tw-relative tw-flex tw-items-center tw-border-b-2 tw-border-dotted tw-border-black/10 tw-py-3 tw-transition-colors last:tw-border-b-0",
        item.audioUrl && "tw-cursor-pointer",
      )}
    >
      {/* Thumbnail + Title + Meta */}
      <div className='lg:tw-pr-10 tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-4 lg:tw-flex-[3]'>
        <AudioRowPlayControl
          id={item.id}
          audioUrl={item.audioUrl}
          thumbnailImageProps={item.thumbnailImageProps}
          title={item.title}
          onPlay={onPlay ? () => onPlay(item) : undefined}
          knownDuration={item.durationSeconds}
        />
        <div className='tw-flex tw-grow tw-gap-4 tw-min-w-0 tw-flex-col tw-justify-center'>
          <p className='tw-type-base tw-leading-[1.2] tw-text-black'>
            <span className='tw-line-clamp-2 lg:tw-line-clamp-none lg:tw-truncate tw-text-pretty'>
              {item.summary || item.title}
            </span>
          </p>
          <p className='tw-type-sm tw-leading-[1.2] tw-text-gray-500'>
            {metaText ||
              (item.audioUrl ? (
                <span className='tw-inline-block tw-h-3.5 tw-w-8 tw-animate-pulse tw-rounded tw-bg-gray-200 tw-align-middle' />
              ) : null)}
          </p>
        </div>
      </div>

      {/* Downloads column — hidden on mobile, hidden entirely when no item has a count */}
      {showDownloadsColumn && (
        <div className='tw-hidden tw-flex-1 tw-items-center tw-pr-[60px] lg:tw-flex'>
          <p className='tw-type-base tw-text-black'>
            {item.downloadCount != null ? format.number(item.downloadCount) : "\u2014"}
          </p>
        </div>
      )}

      {/* Action buttons — sit above the invisible row overlay button */}
      <div data-action-buttons className='tw-z-[1] tw-relative tw-flex tw-shrink-0 tw-items-center'>
        {onEditPrompt ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant='ghost-warm'
                  size='pill'
                  aria-label={t("edit_prompt_label")}
                  icon={<IconWriting className={cn(iconStrokeFix, "tw-size-5")} />}
                  onClick={onEditPrompt ? () => onEditPrompt(item) : undefined}
                />
              </TooltipTrigger>
              <TooltipContent variant='dark-new'>{t("edit_prompt_label")}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : null}
        {onDownload ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant='ghost-warm'
                  size='pill'
                  aria-label={t("download")}
                  icon={<IconArrowInbox className={cn(iconStrokeFix, "tw-size-5")} />}
                  onClick={onDownload ? () => onDownload(item) : undefined}
                />
              </TooltipTrigger>
              <TooltipContent variant='dark-new'>{t("download")}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : null}
      </div>
    </div>
  );
}

const CategoryExploreList = ({
  items,
  categoryName,
  totalCount,
  generateCtaHref,
  generateCtaLabel,
  generateCtaPosition = 3,
  generateCtaShowOnMobile,
  generateFooterTitle,
  generateFooterSubtitle,
  onDownload,
  onEditPrompt,
  onGenerateSfx,
  onPlay,
  durationFormat,
  labels,
}: CategoryExploreListProps) => {
  const tGlobals = useTranslations("ClientKeys.Globals");
  const tList = useTranslations("ClientKeys.CategoryExploreList");

  const count = totalCount ?? items.length;
  const headerText = [String(count), categoryName, labels.headerCountSuffix].filter(Boolean).join(" ");
  const showDownloadsColumn = items.some(item => item.downloadCount != null);

  // Build rows, inserting the CTA row at generateCtaPosition (or at end if beyond bounds)
  const ctaLabel = generateCtaLabel ?? labels.generateCtaDefaultLabel;
  const ctaPosition = generateCtaHref ? generateCtaPosition : -1;

  const rows = items.flatMap((item, index) => {
    const audioRow = (
      <AudioRow
        key={item.id}
        item={item}
        showDownloadsColumn={showDownloadsColumn}
        onDownload={onDownload}
        onEditPrompt={onEditPrompt}
        onPlay={onPlay}
        durationFormat={durationFormat}
      />
    );
    if (index === ctaPosition) {
      return [
        <GenerateCtaRow
          key='generate-cta'
          href={generateCtaHref!}
          label={ctaLabel}
          onGenerateSfx={onGenerateSfx}
          buttonLabel={tList("generate_cta_button")}
          showOnMobile={generateCtaShowOnMobile}
        />,
        audioRow,
      ];
    }
    return [audioRow];
  });

  if (generateCtaHref && ctaPosition >= items.length) {
    rows.push(
      <GenerateCtaRow
        key='generate-cta'
        href={generateCtaHref}
        label={ctaLabel}
        onGenerateSfx={onGenerateSfx}
        buttonLabel={tList("generate_cta_button")}
        showOnMobile={generateCtaShowOnMobile}
      />,
    );
  }

  return (
    <div className='tw-flex tw-flex-col'>
      {/* Header — structure exactly mirrors AudioRow columns */}
      <div className='tw-text-gray-500 tw-flex tw-items-center tw-pt-2 tw-pb-2 lg:tw-pb-4'>
        {/* Col 1: mirrors left col of AudioRow */}
        <div className='lg:tw-pr-10 tw-flex tw-min-w-0 tw-flex-1 tw-items-center lg:tw-flex-[3]'>
          <p className='tw-type-sm tw-min-w-0'>{headerText}</p>
        </div>
        {/* Col 2: mirrors downloads col of AudioRow */}
        {showDownloadsColumn && (
          <div className='tw-hidden tw-flex-1 tw-items-center tw-pr-[60px] lg:tw-flex'>
            <p className='tw-type-sm'>{tGlobals("downloads")}</p>
          </div>
        )}
        {/* Col 3: invisible spacer mirrors the same conditional action buttons */}
        <div className='tw-invisible tw-hidden tw-shrink-0 tw-items-center lg:tw-flex' aria-hidden='true'>
          {onEditPrompt ? <div className='tw-w-10' /> : null}
          {onDownload ? <div className='tw-w-10' /> : null}
        </div>
      </div>
      <div className='tw-border-b-2 tw-border-dotted tw-border-black/10' />

      {/* Rows */}
      {rows}

      {/* Footer CTA */}
      {generateCtaHref && generateFooterTitle && (
        <GenerateFooter
          href={generateCtaHref}
          title={generateFooterTitle}
          subtitle={generateFooterSubtitle}
          onGenerateSfx={onGenerateSfx}
          buttonLabel={tList("generate_footer_button")}
        />
      )}
    </div>
  );
};

CategoryExploreList.displayName = "CategoryExploreList";

export { CategoryExploreList };
export type { SoundEffectExploreItem, CategoryExploreListProps, CategoryExploreListLabels };
